import axios from 'axios';
import type { TokenDataResponse, AccessInfoResponse } from '@pages/Analytics/types';

/**
 * Create analytics-specific axios instance for copilot API endpoints
 */
const analyticsInstance = axios.create({
    baseURL: '/api/copilot',
});

/**
 * Request interceptor: Attach JWT token to all requests
 */
analyticsInstance.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('idToken');
        if (token) {
            config.headers['Authorization'] = `Bearer ${token}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

/**
 * Response interceptor: Handle errors globally
 */
analyticsInstance.interceptors.response.use(
    (response) => response,
    (error) => {
        const status = error.response?.status;
        if (status === 401 || status === 403) {
            console.warn(`Analytics API authentication error (${status})`);
        }
        return Promise.reject(error);
    }
);

export interface TokenDataParams {
    start_time?: string;
    end_time?: string;
    API_TYPE?: string;
    PROJECT_TYPE?: string;
    env?: string;
}

/**
 * Get token usage data with optional filtering
 * @param params - Query parameters for filtering token data
 * @returns Promise with token data response
 */
export const getTokenData = async (params?: TokenDataParams): Promise<TokenDataResponse> => {
    const response = await analyticsInstance.get('/get_tokens_with_user_info', {
        params,
    });
    return response.data;
};

/**
 * Get user access information for authentication
 * @returns Promise with access info response
 */
export const getAccessInfo = async (): Promise<AccessInfoResponse> => {
    const response = await analyticsInstance.get('/user/accessinfo');
    return response.data;
};

/**
 * Export analytics data (future enhancement)
 * @param params - Query parameters for export
 * @returns Promise with blob data for download
 */
export const exportAnalyticsData = async (params?: TokenDataParams): Promise<Blob> => {
    const response = await analyticsInstance.get('/export_token_data', {
        params,
        responseType: 'blob',
    });
    return response.data;
};
